<template>
  <div
    :class="inline ? '' : 'offcanvas offcanvas-fullscreen-sm-down offcanvas-end'"
    :style="inline ? 'display: contents;' : 'width: 820px; max-width: 100%;'"
    :tabindex="inline ? null : -1"
    ref="offcanvas"
    :data-bs-backdrop="inline ? null : 'static'"
    :data-bs-scroll="inline ? null : 'true'"
  >
    <!-- Header -->
    <div v-if="!inline" class="offcanvas-header bg-info py-3">
      <div>
        <h5 class="offcanvas-title fw-bold mb-0 text-dark">
          <i class="fa-solid fa-object-group me-2" />
          {{ $t('Merge Bill') }}
        </h5>
        <div class="small text-dark opacity-75 mt-1">
          {{ sessionName }} &nbsp;|&nbsp; {{ $t('Combine multiple bills into one') }}
        </div>
      </div>
      <button type="button" class="btn-close" @click="handleClose" />
    </div>

    <!-- Body -->
    <div class="offcanvas-body p-3" style="background: #f7f9fc;">

      <!-- Summary -->
      <div class="row g-2 mb-3">
        <div class="col-6">
          <div class="card border-0 bg-white shadow-sm rounded-3">
            <div class="card-body p-3">
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <div class="text-muted small">
                    <i class="fa-solid fa-list-check me-1"></i> {{ $t('Selected') }}
                  </div>
                  <div class="fw-bold fs-5 mt-1" :class="{ 'text-info': selectedCount > 0 }">
                    {{ selectedCount }} / {{ mergeableBills.length }}
                  </div>
                </div>
                <span class="badge bg-secondary-subtle text-secondary rounded-pill">
                  {{ $t('bills') }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="col-6">
          <div
            class="card border-0 shadow-sm rounded-3"
            :class="canConfirm ? 'bg-info bg-opacity-10 border border-info' : 'bg-white'"
          >
            <div class="card-body p-3">
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <div class="text-muted small">
                    <i class="fa-solid fa-file-circle-check me-1"></i> {{ $t('Merged Total') }}
                  </div>
                  <div class="fw-bold fs-5 mt-1" :class="{ 'text-info': canConfirm }">
                    {{ $storeIndex.formatThaiCurrency(mergedGrandTotal) }}
                  </div>
                </div>
                <span
                  class="badge rounded-pill"
                  :class="canConfirm ? 'bg-info text-white' : 'bg-secondary-subtle text-secondary'"
                >
                  {{ mergedItemCount }} {{ $t('items') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="d-flex gap-2 mb-3">
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary rounded-3"
          :disabled="selectedCount === 0 || submitting"
          @click="clearAll"
        >
          <i class="fa-solid fa-rotate-left me-1"></i> {{ $t('Clear') }}
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-info rounded-3"
          :disabled="!canSelectAll || submitting"
          @click="selectAll"
        >
          <i class="fa-solid fa-check-double me-1"></i> {{ $t('Select All') }}
        </button>
      </div>

      <!-- Bills list -->
      <div class="d-flex align-items-center justify-content-between mb-2 px-1">
        <span class="fw-bold text-dark">
          <i class="fa-solid fa-file-invoice-dollar me-2 text-muted"></i>{{ $t('Choose bills to merge') }}
        </span>
        <span v-if="targetId" class="text-muted small">
          <i class="fa-solid fa-bullseye me-1"></i>{{ $t('Items merge into the Target bill') }}
        </span>
      </div>

      <div
        v-for="(bill, idx) in bills"
        :key="bill.id"
        class="bg-white rounded-3 p-3 mb-2 shadow-sm"
        :class="{
          'opacity-50': bill.paid,
          'border border-info': !bill.paid && isSelected(bill.id),
          'border-2': !bill.paid && bill.id === targetId
        }"
        :style="bill.paid || submitting ? '' : 'cursor: pointer;'"
        @click="bill.paid || submitting ? null : toggle(bill.id)"
      >
        <div class="d-flex align-items-start gap-2">
          <!-- Checkbox -->
          <div class="form-check mt-1">
            <input
              type="checkbox"
              class="form-check-input"
              :checked="isSelected(bill.id)"
              :disabled="bill.paid || submitting"
              @click.stop
              @change="toggle(bill.id)"
            />
          </div>

          <!-- Bill info -->
          <div style="flex: 1; min-width: 0;">
            <div class="d-flex align-items-center gap-2 flex-wrap">
              <span class="fw-semibold">
                {{ $t('Bill') }} {{ idx + 1 }}
              </span>
              <span
                v-if="bill.id === targetId"
                class="badge bg-info text-white"
                style="font-size: 0.7rem;"
                :title="$t('Other bills will merge into this one and keep its bill number')"
              >
                <i class="fa-solid fa-bullseye me-1"></i>{{ $t('Target') }}
              </span>
              <button
                v-else-if="isSelected(bill.id) && !submitting"
                type="button"
                class="btn btn-sm btn-link p-0 text-info text-decoration-none"
                style="font-size: 0.7rem;"
                @click.stop="setAsTarget(bill.id)"
                :title="$t('Make this bill the merge target')"
              >
                <i class="fa-solid fa-bullseye me-1"></i>{{ $t('Set as target') }}
              </button>
              <span
                v-if="bill.paid"
                class="badge bg-success-subtle text-success"
                style="font-size: 0.7rem;"
              >
                <i class="fa-solid fa-check me-1"></i>{{ $t('Paid') }}
              </span>
              <span
                v-else
                class="badge bg-warning-subtle text-warning"
                style="font-size: 0.7rem;"
              >
                {{ $t('Unpaid') }}
              </span>
            </div>
            <div class="text-muted small">
              {{ bill.bill_no }} &nbsp;·&nbsp;
              {{ activeItems(bill).length }} {{ $t('items') }}
            </div>
            <!-- Item preview -->
            <div v-if="activeItems(bill).length > 0" class="d-flex flex-wrap gap-1 mt-2">
              <span
                v-for="(item, idx2) in activeItems(bill).slice(0, 4)"
                :key="idx2"
                class="badge bg-light text-dark border rounded-pill fw-normal"
                style="font-size: 0.7rem;"
              >
                {{ $t(item.name) }}<span class="text-muted ms-1">x{{ item.quantity }}</span>
              </span>
              <!-- "+N" badge with a hover/focus popover listing the remaining items -->
              <span
                v-if="activeItems(bill).length > 4"
                class="items-popover-host"
                tabindex="0"
                :aria-label="$t('Show remaining items')"
                @click.stop
              >
                <span
                  class="badge bg-light text-muted border rounded-pill fw-normal"
                  style="font-size: 0.7rem; cursor: help;"
                >
                  +{{ activeItems(bill).length - 4 }}
                </span>
                <span class="items-popover" role="tooltip">
                  <span class="items-popover-title">
                    {{ $t('Remaining items') }} ({{ activeItems(bill).length - 4 }})
                  </span>
                  <span
                    v-for="(item, idx3) in activeItems(bill).slice(4)"
                    :key="idx3"
                    class="items-popover-row"
                  >
                    <span class="items-popover-name">{{ item.name }}</span>
                    <span class="items-popover-qty">x{{ item.quantity }}</span>
                  </span>
                </span>
              </span>
            </div>
          </div>

          <!-- Total -->
          <div class="text-end">
            <div class="fw-bold">
              {{ $storeIndex.formatThaiCurrency(bill.grand_total) }}
            </div>
            <div class="text-muted" style="font-size: 0.7rem;">
              {{ $t('Grand Total') }}
            </div>
          </div>
        </div>
      </div>

      <!-- No mergeable bills warning -->
      <div v-if="mergeableBills.length < 2" class="alert alert-warning mt-3 mb-0 rounded-3">
        <i class="fa-solid fa-triangle-exclamation me-2"></i>
        {{ $t('Need at least 2 unpaid bills to merge') }}
      </div>

    </div>

    <!-- Footer -->
    <div class="bg-white p-3 border-top d-flex gap-2">
      <button
        type="button"
        class="btn btn-outline-secondary rounded-3 px-4 flex-fill"
        :disabled="submitting"
        @click="handleClose"
      >
        <i class="fa-solid fa-arrow-left me-1"></i> {{ inline ? $t('Back') : $t('Cancel') }}
      </button>
      <button
        type="button"
        class="btn btn-info text-white rounded-3 px-4 fw-bold flex-fill"
        :disabled="!canConfirm || submitting"
        @click="handleConfirm"
      >
        <i class="fa-solid fa-spinner fa-spin me-1" v-if="submitting"></i>
        <i class="fa-solid fa-check me-1" v-else></i>
        {{ $t('Confirm Merge') }}
        <span v-if="selectedCount > 0" class="ms-1">
          ({{ selectedCount }} {{ $t('bills') }})
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import { useScrollRestore } from '@/composables/useScrollRestore';

export default {
  name: 'OffcanvasMergeBill',
  props: {
    sessionName: { type: String, default: '' },
    bills: { type: Array, required: true },
    inline: { type: Boolean, default: false },
    // Default target: caller can pass the currently-viewed bill id so it's
    // pre-selected as `ids[0]` (the API uses index 0 as the merge target).
    defaultTargetId: { type: [Number, String], default: null },
    // Parent controls submit state to show the spinner during the API call.
    submitting: { type: Boolean, default: false }
  },
  data() {
    return {
      // Array preserves selection order — element [0] becomes the API target.
      selectedOrder: []
    };
  },
  computed: {
    mergeableBills() {
      return this.bills.filter(b => !b.paid);
    },
    selectedBills() {
      // Map ids back to bills in selection order (drop any that disappeared).
      return this.selectedOrder
        .map(id => this.bills.find(b => b.id === id))
        .filter(Boolean);
    },
    selectedCount() {
      return this.selectedBills.length;
    },
    targetId() {
      return this.selectedOrder.length > 0 ? this.selectedOrder[0] : null;
    },
    mergedItemCount() {
      return this.selectedBills.reduce(
        (sum, b) => sum + this.activeItems(b).length,
        0
      );
    },
    // Sum of grand totals — note the backend recalculates the actual merged
    // total, dropping bill-level discount/shipping/service of the merged-in
    // bills. This figure is a best-effort preview only.
    mergedGrandTotal() {
      return this.selectedBills.reduce((sum, b) => sum + (b.grand_total || 0), 0);
    },
    canConfirm() {
      return this.selectedCount >= 2;
    },
    canSelectAll() {
      return this.mergeableBills.length >= 2 && this.selectedCount < this.mergeableBills.length;
    }
  },
  methods: {
    activeItems(bill) {
      return (bill.items || []).filter(i => !i.cancelled);
    },
    isSelected(id) {
      return this.selectedOrder.includes(id);
    },
    toggle(id) {
      const idx = this.selectedOrder.indexOf(id);
      if (idx >= 0) {
        this.selectedOrder = [
          ...this.selectedOrder.slice(0, idx),
          ...this.selectedOrder.slice(idx + 1)
        ];
      } else {
        this.selectedOrder = [...this.selectedOrder, id];
      }
    },
    setAsTarget(id) {
      if (!this.isSelected(id)) return;
      // Move id to index 0; preserve order of the rest.
      const others = this.selectedOrder.filter(x => x !== id);
      this.selectedOrder = [id, ...others];
    },
    selectAll() {
      // Keep already-selected order, then append the rest.
      const remaining = this.mergeableBills
        .map(b => b.id)
        .filter(id => !this.selectedOrder.includes(id));
      this.selectedOrder = [...this.selectedOrder, ...remaining];
    },
    clearAll() {
      this.selectedOrder = [];
    },
    handleClose() {
      if (this.inline) {
        this.$emit('back');
      } else {
        $(this.$refs.offcanvas).offcanvas('hide');
      }
    },
    handleConfirm() {
      if (!this.canConfirm) return;
      // Emit the exact API body — parent posts it as-is.
      this.$emit('confirmed', { ids: [...this.selectedOrder] });
    }
  },
  mounted() {
    // Default selection: prefer the caller-provided target, otherwise the
    // first two unpaid bills in list order.
    const seed = [];
    const defaultId = this.defaultTargetId;
    if (defaultId != null) {
      const match = this.mergeableBills.find(b => b.id === defaultId);
      if (match) seed.push(match.id);
    }
    for (const b of this.mergeableBills) {
      if (seed.length >= 2) break;
      if (!seed.includes(b.id)) seed.push(b.id);
    }
    this.selectedOrder = seed;

    if (this.inline) return;
    $(this.$refs.offcanvas).offcanvas('show');
    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$emit('close');
    });
    useScrollRestore(() => this.$refs.offcanvas?.querySelector('.offcanvas-body'));
  }
};
</script>

<style scoped>
/* Hover/focus popover used for the "+N" remaining-items badge. */
.items-popover-host {
  position: relative;
  display: inline-flex;
  outline: none;
}
.items-popover {
  display: none;
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: #212529;
  color: #fff;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 0.7rem;
  z-index: 1080;
  min-width: 160px;
  max-width: 240px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  white-space: normal;
  text-align: left;
  /* Build the popover with flex so each row stays on its own line. */
  flex-direction: column;
  gap: 2px;
}
.items-popover-host:hover .items-popover,
.items-popover-host:focus-within .items-popover {
  display: flex;
}
.items-popover::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #212529;
}
.items-popover-title {
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  padding-bottom: 4px;
  margin-bottom: 4px;
}
.items-popover-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}
.items-popover-name {
  flex: 1;
  word-break: break-word;
}
.items-popover-qty {
  opacity: 0.75;
  white-space: nowrap;
}
</style>
